import {
    activityCategories,
    activityCategoryLabels,
    OutcomeGoalMetric,
    outcomeGoalMetricLabels,
    outcomeGoalMetrics,
    RegionId,
    unknownActivityTypeLabel,
    UserId,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { goalActivityTypeOptions } from "components/marketing-goals/goal-columns";
import {
    activityCategoryBarColor,
    currentYearMonth,
    outcomeGoalMetricBarColor,
    ymAddMonths,
    ymLabel,
} from "components/marketing-goals/pace";
import { trpc } from "components/rpc/trpc";
import {
    byActivityTypeName,
    useActivityTypeOptionsState,
} from "components/tasks/activity-type-options";
import { DeleteButton } from "components/ui/action-button";
import { Input } from "components/ui/input";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton, SecondaryCompactButton } from "components/ui/secondary-button";
import { Select } from "components/ui/select";
import { Spinner } from "components/ui/spinner";
import React, { useEffect, useMemo, useState } from "react";

type RouterOutput = inferRouterOutputs<AppRouter>;
type GoalVersion = RouterOutput["marketingGoal"]["marketerVersions"][number];

export interface EditorMarketer {
    id: UserId;
    name: string;
}

interface GoalEditorProps {
    regionId: RegionId;
    fixedMarketerId: UserId | null;
    marketers: EditorMarketer[];
    now: Date;
    onSaved: (effectiveMonth: string) => void;
    onCancel: () => void;
}

type EditorRow =
    | { kind: "activity"; key: string; label: string; barColor: string }
    | { kind: "outcome"; key: OutcomeGoalMetric; label: string; barColor: string };

interface EditorGroup {
    title: string;
    rows: EditorRow[];
}

export const resolveActiveVersion = (
    versions: GoalVersion[],
    month: string,
): GoalVersion | null => {
    let active: GoalVersion | null = null;
    for (const version of [...versions].sort((a, b) =>
        a.effectiveMonth.localeCompare(b.effectiveMonth),
    )) {
        if (version.effectiveMonth <= month) {
            active = version;
        }
    }
    return active;
};

const versionTargets = (version: GoalVersion): Record<string, number> => ({
    ...version.activity,
    referrals: version.referrals,
    careStarts: version.careStarts,
});

const trackedFromVersion = (
    version: GoalVersion | null,
    defaultKeys: string[],
): Set<string> => {
    if (!version) {
        return new Set(defaultKeys);
    }
    const targets = versionTargets(version);
    const tracked = Object.keys(targets).filter(key => targets[key] > 0);
    return new Set(tracked.length ? tracked : defaultKeys);
};

export const GoalEditorPanel: React.FC<GoalEditorProps> = ({
    regionId,
    fixedMarketerId,
    marketers,
    now,
    onSaved,
    onCancel,
}) => {
    const isEdit = !!fixedMarketerId;
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();
    const { options: activityTypeOptions, isLoading: optionsLoading } =
        useActivityTypeOptionsState();
    const currentMonth = currentYearMonth(now);
    const [selectedId, setSelectedId] = useState<UserId | null>(fixedMarketerId);
    const [applyMonth, setApplyMonth] = useState<string>(currentMonth);
    const [vals, setVals] = useState<Record<string, number>>({});
    const [tracked, setTracked] = useState<Set<string>>(new Set());
    const [saveError, setSaveError] = useState<string | null>(null);

    const monthOptions = useMemo(
        () => Array.from({ length: 7 }, (_, index) => ymAddMonths(currentMonth, index)),
        [currentMonth],
    );

    const versionsQuery = useQuery({
        ...trpcClient.marketingGoal.marketerVersions.queryOptions({
            regionId,
            marketerUserId: selectedId ?? ("" as UserId),
        }),
        enabled: !!selectedId,
    });

    const activeVersion = useMemo(
        () =>
            selectedId
                ? resolveActiveVersion(versionsQuery.data ?? [], applyMonth)
                : null,
        [selectedId, versionsQuery.data, applyMonth],
    );

    const groups: EditorGroup[] = useMemo(() => {
        const versionActivityKeys = Object.keys(activeVersion?.activity ?? {});
        const availableOptions = goalActivityTypeOptions(
            activityTypeOptions,
            new Set(versionActivityKeys),
        );
        const knownIds = new Set<string>(availableOptions.map(option => option.id));
        const activityGroups = activityCategories
            .map(category => {
                const rows = availableOptions
                    .filter(
                        option =>
                            option.category === category &&
                            (!option.isArchived ||
                                versionActivityKeys.includes(option.id)),
                    )
                    .sort(byActivityTypeName)
                    .map<EditorRow>(option => ({
                        kind: "activity",
                        key: option.id as string,
                        label: option.name,
                        barColor: activityCategoryBarColor[category],
                    }));
                if (category === "other") {
                    for (const key of versionActivityKeys) {
                        if (!knownIds.has(key)) {
                            rows.push({
                                kind: "activity",
                                key,
                                label: unknownActivityTypeLabel,
                                barColor: activityCategoryBarColor.other,
                            });
                        }
                    }
                }
                return { title: activityCategoryLabels[category], rows };
            })
            .filter(group => group.rows.length > 0);
        const outcomeGroup: EditorGroup = {
            title: "Outcome goals",
            rows: outcomeGoalMetrics.map<EditorRow>(metric => ({
                kind: "outcome",
                key: metric,
                label: outcomeGoalMetricLabels[metric],
                barColor: outcomeGoalMetricBarColor[metric],
            })),
        };
        return [...activityGroups, outcomeGroup];
    }, [activityTypeOptions, activeVersion]);

    const allKeys = useMemo(
        () => groups.flatMap(group => group.rows.map(row => row.key)),
        [groups],
    );

    useEffect(() => {
        setVals(activeVersion ? versionTargets(activeVersion) : {});
        setTracked(trackedFromVersion(activeVersion, allKeys));
        setSaveError(null);
    }, [selectedId, applyMonth, activeVersion, allKeys]);

    const upsertMutation = useMutation({
        ...trpcClient.marketingGoal.upsert.mutationOptions(),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: trpcClient.marketingGoal.teamAttainment.queryKey(),
            });
            await queryClient.invalidateQueries({
                queryKey: trpcClient.marketingGoal.marketerVersions.queryKey(),
            });
            onSaved(applyMonth);
        },
        onError: () => setSaveError("Couldn't save goals. Please try again."),
    });

    const setVal = (key: string, raw: string) =>
        setVals(prev => ({
            ...prev,
            [key]: Math.max(0, parseInt(raw || "0", 10) || 0),
        }));

    const removeKey = (key: string) =>
        setTracked(prev => {
            const next = new Set(prev);
            next.delete(key);
            return next;
        });

    const addKey = (key: string) =>
        setTracked(prev => {
            const next = new Set(prev);
            next.add(key);
            return next;
        });

    const valFor = (key: string): number => vals[key] ?? 0;

    const total = allKeys.reduce(
        (sum, key) => sum + (tracked.has(key) ? valFor(key) : 0),
        0,
    );
    const canSave = !!selectedId && total > 0 && !upsertMutation.isPending;

    const handleSave = () => {
        if (!selectedId) {
            return;
        }
        const activity: Record<string, number> = {};
        const outcomes: Record<OutcomeGoalMetric, number> = {
            referrals: 0,
            careStarts: 0,
        };
        for (const row of groups.flatMap(group => group.rows)) {
            const value = tracked.has(row.key) ? valFor(row.key) : 0;
            switch (row.kind) {
                case "activity":
                    if (value > 0) {
                        activity[row.key] = value;
                    }
                    break;
                case "outcome":
                    outcomes[row.key] = value;
                    break;
                default: {
                    const exhaustiveCheck: never = row;
                    throw new Error(`Unhandled editor row ${String(exhaustiveCheck)}`);
                }
            }
        }
        upsertMutation.mutate({
            regionId,
            marketerUserId: selectedId,
            effectiveMonth: applyMonth,
            activity,
            referrals: outcomes.referrals,
            careStarts: outcomes.careStarts,
        });
    };

    return (
        <Panel
            isOpen={true}
            onDismiss={onCancel}
            type={PanelType.large}
            headerText={isEdit ? "Edit goals" : "Create a goal"}>
            <div className="space-y-5">
                {!isEdit ? (
                    <Select
                        label="Marketer"
                        textSize="sm"
                        value={selectedId ?? ""}
                        onChange={event =>
                            setSelectedId((event.target.value || null) as UserId | null)
                        }>
                        <option value="">Select a marketer…</option>
                        {marketers.map(marketer => (
                            <option key={marketer.id} value={marketer.id}>
                                {marketer.name}
                            </option>
                        ))}
                    </Select>
                ) : null}

                {!selectedId ? (
                    <div className="rounded-lg bg-zinc-50 px-3 py-8 text-center text-sm text-zinc-500 ring-1 ring-zinc-200">
                        Select a marketer to set their goals.
                    </div>
                ) : (
                    <div className="space-y-5">
                        {optionsLoading ? (
                            <div className="flex justify-center py-8">
                                <Spinner />
                            </div>
                        ) : (
                            groups.map(group => {
                                const shown = group.rows.filter(row =>
                                    tracked.has(row.key),
                                );
                                const removed = group.rows.filter(
                                    row => !tracked.has(row.key),
                                );
                                return (
                                    <div key={group.title}>
                                        <div className="mb-1.5 text-sm font-semibold text-zinc-700">
                                            {group.title}
                                        </div>
                                        <div className="divide-y divide-zinc-100 overflow-hidden rounded-lg ring-1 ring-zinc-200">
                                            {shown.length === 0 ? (
                                                <div className="px-3 py-2.5 text-sm text-zinc-400">
                                                    None added.
                                                </div>
                                            ) : null}
                                            {shown.map(row => (
                                                <div
                                                    key={row.key}
                                                    className="flex items-center gap-3 bg-white px-3 py-2">
                                                    <span
                                                        className={`inline-block size-2.5 flex-shrink-0 rounded-sm ${row.barColor}`}
                                                    />
                                                    <span className="min-w-0 flex-1 truncate text-sm text-zinc-800">
                                                        {row.label}
                                                    </span>
                                                    <div className="w-24 flex-none">
                                                        <Input
                                                            type="number"
                                                            min="0"
                                                            textSize="sm"
                                                            value={String(
                                                                valFor(row.key),
                                                            )}
                                                            onChange={event =>
                                                                setVal(
                                                                    row.key,
                                                                    event.target.value,
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                    <div className="flex-none">
                                                        <DeleteButton
                                                            backgroundColor="bg-white"
                                                            onClick={() =>
                                                                removeKey(row.key)
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                            {removed.length > 0 ? (
                                                <div className="flex flex-wrap gap-2 bg-zinc-50/60 px-3 py-2">
                                                    {removed.map(row => (
                                                        <SecondaryCompactButton
                                                            key={row.key}
                                                            onClick={() =>
                                                                addKey(row.key)
                                                            }>
                                                            + {row.label}
                                                        </SecondaryCompactButton>
                                                    ))}
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                );
                            })
                        )}

                        <Select
                            label="Apply starting"
                            textSize="sm"
                            className="max-w-xs"
                            value={applyMonth}
                            onChange={event => setApplyMonth(event.target.value)}>
                            {monthOptions.map(month => (
                                <option key={month} value={month}>
                                    {ymLabel(month)}
                                    {month === currentMonth ? " (this month)" : ""}
                                </option>
                            ))}
                        </Select>
                    </div>
                )}

                {saveError ? (
                    <div className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700 ring-1 ring-rose-200">
                        {saveError}
                    </div>
                ) : null}

                <div className="flex items-center justify-end gap-2 border-t border-zinc-100 pt-4">
                    <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
                    <PrimaryButton disabled={!canSave} onClick={handleSave}>
                        {isEdit ? "Save goals" : "Create goal"}
                    </PrimaryButton>
                </div>
            </div>
        </Panel>
    );
};
